import { useQuery } from "@tanstack/react-query";
import {
  fetchPromptCitations,
  fetchPromptDetail,
  fetchPromptHistory,
  fetchPromptLibrary,
  fetchPromptResponses,
  fetchPromptTable,
  fetchPromptTrend,
} from "../api/index.js";
import { queryKeys } from "../query-keys.js";

/** The prompt library, the question table, and the panels behind one question. */

export function usePromptLibrary(workspaceId: string, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.promptLibrary(workspaceId),
    queryFn: () => fetchPromptLibrary(workspaceId),
    enabled,
  });
}

export function usePromptTable(
  workspaceId: string,
  reportId: string,
  range: {
    readonly from: string;
    readonly to: string;
    readonly engine?: string;
  },
  /** False until the range is known, as for the days before one. */
  enabled = true,
) {
  return useQuery({
    enabled,
    queryKey: [
      "prompt-table",
      workspaceId,
      reportId,
      range.from,
      range.to,
      range.engine ?? "all",
    ],
    queryFn: () => fetchPromptTable(workspaceId, reportId, range),
    /*
     * Refetch while anything is still being captured, and stop when it is not.
     *
     * A question subscribed a moment ago has captures asked for and no answers,
     * and the answers arrive over the next few minutes. Without this the table
     * a person is watching stays empty until they think to reload, which reads
     * as broken rather than as pending.
     *
     * `reading` is in the condition for a reason found the hard way: polling
     * that stopped at `awaiting` stopped the moment the answers landed, a full
     * minute before anything had been read out of them. The table then sat
     * showing a row with answers and no findings and never moved again, which
     * is exactly the "it never ran" this was supposed to prevent.
     *
     * The interval is a function of the data rather than a constant, so a
     * finished report costs nothing: `false` ends the polling entirely. Ten
     * seconds is slower than a capture completes and fast enough that a row
     * filling in feels like the page noticing.
     */
    refetchInterval: (query) =>
      query.state.data?.rows.some((row) => row.awaiting > 0 || row.reading > 0) === true
        ? 10_000
        : false,
  });
}

/**
 * The line and the dots beside each row of the Questions table. Polled on the
 * same rule as the table: while a capture is waiting or being read, a dot is
 * about to change.
 */
export function usePromptTrend(
  workspaceId: string,
  reportId: string,
  range: {
    readonly from: string;
    readonly to: string;
    readonly engine?: string;
  },
) {
  return useQuery({
    queryKey: [
      "prompt-trend",
      workspaceId,
      reportId,
      range.from,
      range.to,
      range.engine ?? "all",
    ],
    queryFn: () => fetchPromptTrend(workspaceId, reportId, range),
    refetchInterval: (query) =>
      query.state.data?.rows.some((row) =>
        row.latest.some((last) => last.state === "waiting" || last.state === "reading"),
      ) === true
        ? 10_000
        : false,
  });
}

export function usePromptDetail(
  workspaceId: string,
  reportId: string,
  promptId: string | null,
  range: {
    readonly from: string;
    readonly to: string;
    readonly engine?: string;
  },
) {
  return useQuery({
    queryKey: [
      "prompt-detail",
      workspaceId,
      reportId,
      promptId ?? "",
      range.from,
      range.to,
      range.engine ?? "all",
    ],
    queryFn: () => fetchPromptDetail(workspaceId, reportId, promptId ?? "", range),
    enabled: promptId !== null,
  });
}

export function usePromptResponses(
  workspaceId: string,
  {
    reportId,
    promptId,
    range,
    enabled,
  }: {
    readonly reportId: string;
    readonly promptId: string;
    readonly range: {
      readonly from: string;
      readonly to: string;
      readonly engine?: string;
    };
    readonly enabled: boolean;
  },
) {
  return useQuery({
    queryKey: [
      "prompt-responses",
      workspaceId,
      reportId,
      promptId,
      range.from,
      range.to,
      range.engine ?? "all",
    ],
    queryFn: () => fetchPromptResponses(workspaceId, reportId, promptId, range),
    enabled,
  });
}

export function usePromptCitations(
  workspaceId: string,
  {
    reportId,
    promptId,
    range,
    enabled,
  }: {
    readonly reportId: string;
    readonly promptId: string;
    readonly range: {
      readonly from: string;
      readonly to: string;
      readonly engine?: string;
    };
    readonly enabled: boolean;
  },
) {
  return useQuery({
    queryKey: [
      "prompt-citations",
      workspaceId,
      reportId,
      promptId,
      range.from,
      range.to,
      range.engine ?? "all",
    ],
    queryFn: () => fetchPromptCitations(workspaceId, reportId, promptId, range),
    enabled,
  });
}

/** One question's captures day by day: the grid and the counts on its page. */
export function usePromptHistory(
  workspaceId: string,
  reportId: string,
  promptId: string,
  range: {
    readonly from: string;
    readonly to: string;
    readonly engine?: string;
  },
) {
  return useQuery({
    queryKey: [
      "prompt-history",
      workspaceId,
      reportId,
      promptId,
      range.from,
      range.to,
      range.engine ?? "all",
    ],
    queryFn: () => fetchPromptHistory(workspaceId, reportId, promptId, range),
    refetchInterval: (query) =>
      query.state.data?.rows.some(
        (row) => row.state === "waiting" || row.state === "reading",
      ) === true
        ? 10_000
        : false,
  });
}
